import Swiper from "swiper";
import { FreeMode, Mousewheel, Navigation } from "swiper/modules";

import Menu from "./menu/menu.js";
import { initAutocomplete } from "../../../js/autocomplete.js";

class BeckPageHeader extends HTMLElement {
	#headerContainer;
	#lastScrollY;
	#navSwiper;
	#fullHeaderHeight = 0;
	#menu;
	#menuButtons;
	#navGradientStart;
	#navGradientEnd;
	#headerResizeObserver;
	#wallpaperResizeObserver;

	#isCompact = false;
	#isSearchOpen = false;
	#SCROLL_THRESHOLD = 5; // Minimum scroll position to enable header state changes
	#RESTORE_HEADER_THRESHOLD = 0;
	#MIN_SCROLL_DELTA = 3; // Minimum scroll distance to trigger compact/expand transitions

	constructor() {
		super();
	}

	connectedCallback() {
		this.#headerContainer = this.querySelector(".Header");

		const multiUnitWallpaperAd = document.querySelector(
			".Template-multiUnitWallpaperAd",
		);

		if (multiUnitWallpaperAd) {
			this.#RESTORE_HEADER_THRESHOLD = multiUnitWallpaperAd.offsetHeight;
			this.#observeWallpaperSize(multiUnitWallpaperAd);
		}

		// One initial synchronous read to seed #fullHeaderHeight before the
		// first call to #updateHeaderState needs it. Subsequent updates come
		// from ResizeObserver so we never have to toggle classes to re-measure.
		this.#fullHeaderHeight = this.#headerContainer.offsetHeight;
		this.#observeHeaderSize();

		this.#lastScrollY = window.scrollY;
		const currentScrollY = window.scrollY;
		this.#updateHeaderState(currentScrollY, currentScrollY);
		this.#addEventListeners();
		this.#initNavSwiper();
		this.#initMenu();
		this.#initSearch();
	}

	disconnectedCallback() {
		window.removeEventListener("scroll", this.#onScroll);
		this.#headerResizeObserver?.disconnect();
		this.#wallpaperResizeObserver?.disconnect();
		this.#destroyNavSwiper();
	}

	#addEventListeners() {
		window.addEventListener("scroll", this.#onScroll, { passive: true });
	}

	/**
	 * Track the header's full (non-compact) height via ResizeObserver.
	 *
	 * Using ResizeObserver avoids the forced synchronous layout that would
	 * otherwise occur if we toggled the compact class to measure on resize.
	 * We only capture the size when the header is in its non-compact state.
	 */
	#observeHeaderSize() {
		this.#headerResizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				if (this.#headerContainer.classList.contains("Header-compact")) {
					continue;
				}
				const borderBox = entry.borderBoxSize?.[0];
				const blockSize = borderBox
					? borderBox.blockSize
					: entry.contentRect.height;
				this.#fullHeaderHeight = blockSize;
				if (this.#headerContainer.classList.contains("Header-fixed")) {
					this.#updateHeaderHeight();
				}
			}
		});
		this.#headerResizeObserver.observe(this.#headerContainer);
	}

	/**
	 * Keep the restore threshold in sync with the live wallpaper/leaderboard
	 * height via ResizeObserver.
	 *
	 * The wallpaper ad loads after consent — long after connectedCallback — so
	 * its height grows from ~0 to its real value once the creative renders. If
	 * the threshold kept the page-load value (~0) the header would only un-fix
	 * at the very top and then snap down by the ad's full height. Tracking the
	 * current height makes the header release exactly where its fixed position
	 * lines up with its in-flow position (just below the wallpaper), so there
	 * is no jump.
	 * @param {HTMLElement} wallpaperEl
	 */
	#observeWallpaperSize(wallpaperEl) {
		this.#wallpaperResizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const borderBox = entry.borderBoxSize?.[0];
				this.#RESTORE_HEADER_THRESHOLD = borderBox
					? borderBox.blockSize
					: entry.contentRect.height;
			}
		});
		this.#wallpaperResizeObserver.observe(wallpaperEl);
	}

	/**
	 * Update header height CSS variable for body padding
	 * Always uses the full header height (initial state) for consistent spacing
	 */
	#updateHeaderHeight() {
		document.documentElement.style.setProperty(
			"--header-height",
			`${this.#fullHeaderHeight}px`,
		);
	}

	/**
	 * Initialize Swiper for horizontal navigation scrolling
	 */
	#initNavSwiper() {
		const navWrapper = this.querySelector("[data-header-nav-swiper]");
		if (!navWrapper) return;

		const prevButton = this.querySelector("[data-nav-prev]");
		const nextButton = this.querySelector("[data-nav-next]");
		this.#navGradientStart = this.querySelector("[data-nav-gradient-start]");
		this.#navGradientEnd = this.querySelector("[data-nav-gradient-end]");

		this.#navSwiper = new Swiper(navWrapper, {
			modules: [Navigation, FreeMode, Mousewheel],
			slidesPerView: "auto",
			spaceBetween: 12,
			freeMode: true,
			grabCursor: true,
			mousewheel: {
				forceToAxis: true,
			},
			navigation: {
				prevEl: prevButton,
				nextEl: nextButton,
			},
			breakpoints: {
				768: {
					spaceBetween: 24,
				},
			},
			on: {
				slideChange: () => {
					this.#updateNavGradients();
				},
				setTranslate: () => {
					this.#updateNavGradients();
				},
				reachBeginning: () => {
					this.#updateNavGradients();
				},
				reachEnd: () => {
					this.#updateNavGradients();
				},
				fromEdge: () => {
					this.#updateNavGradients();
				},
			},
		});

		// Initial update after Swiper is initialized.
		// Defer to rAF so we don't force layout synchronously during connect.
		// A single update() covers size, slides and progress in one pass.
		requestAnimationFrame(() => {
			if (this.#navSwiper) {
				this.#navSwiper.update();
				this.#updateNavGradients();
			}
		});
	}

	/**
	 * Update navigation gradient visibility based on scroll position
	 * Mirrors the disabled state of navigation buttons
	 */
	#updateNavGradients() {
		if (!this.#navSwiper || !this.#navGradientStart || !this.#navGradientEnd) {
			return;
		}

		const prevButton = this.querySelector("[data-nav-prev]");
		const nextButton = this.querySelector("[data-nav-next]");

		// Show start gradient when prev button is NOT disabled (meaning there's content to scroll back to)
		if (
			prevButton &&
			!prevButton.classList.contains("swiper-button-disabled")
		) {
			this.#navGradientStart.classList.add("is-visible");
		} else {
			this.#navGradientStart.classList.remove("is-visible");
		}

		// Show end gradient when next button is NOT disabled (meaning there's content to scroll forward to)
		if (
			nextButton &&
			!nextButton.classList.contains("swiper-button-disabled")
		) {
			this.#navGradientEnd.classList.add("is-visible");
		} else {
			this.#navGradientEnd.classList.remove("is-visible");
		}
	}

	/**
	 * Cleanup Swiper instance on component removal
	 */
	#destroyNavSwiper() {
		if (this.#navSwiper) {
			this.#navSwiper.destroy(true, true);
			this.#navSwiper = null;
		}
	}

	/**
	 * Initialize menu functionality
	 */
	#initMenu() {
		const menuElement = this.querySelector(".js-HeaderMenu");
		if (!menuElement) return;

		// Initialize menu instance
		this.#menu = new Menu(menuElement);

		// Get menu buttons
		this.#menuButtons = this.querySelectorAll(".js-HeaderMenuButton");

		// Connect menu buttons
		if (this.#menuButtons && this.#menuButtons.length > 0) {
			this.#menuButtons.forEach((menuButton) => {
				menuButton.addEventListener("click", (e) => {
					e.preventDefault();
					this.#menu.toggle();

					// Update aria-expanded on all menu buttons
					const isOpen = this.#menu.isOpen;
					this.#menuButtons.forEach((btn) => {
						btn.setAttribute("aria-expanded", isOpen.toString());
					});
				});
			});
		}
	}

	/**
	 * Initialize search functionality for compact mode
	 */
	#initSearch() {
		const searchSubmit = this.querySelector(".Header-searchSubmit");
		const searchInput = this.querySelector(".Header-searchInput");
		const searchForm = this.querySelector(".Header-searchForm");

		if (!searchSubmit || !searchInput || !searchForm) return;

		// Initialize autocomplete on the desktop header search input
		initAutocomplete({
			input: searchInput,
			container: searchForm,
			form: searchForm,
			dropdownClass: "Header-autocomplete",
			itemClass: "Header-autocompleteItem",
		});

		// Initialize autocomplete on the mobile menu search input
		const mobileSearchForm = this.querySelector(".Header-searchFieldInner");
		const mobileSearchInput = mobileSearchForm?.querySelector(
			".Header-searchInput",
		);
		if (mobileSearchForm && mobileSearchInput) {
			initAutocomplete({
				input: mobileSearchInput,
				container: mobileSearchForm,
				form: mobileSearchForm,
				dropdownClass: "Header-autocomplete",
				itemClass: "Header-autocompleteItem",
			});
		}

		searchSubmit.addEventListener("click", (e) => {
			// Only handle click for opening search in compact mode
			// If search is already open or not in compact mode, let the form submit
			if (this.#isCompact && !this.#isSearchOpen) {
				e.preventDefault();
				this.#isSearchOpen = true;
				this.#headerContainer.classList.add("Header-searchOpen");
				searchInput.focus();
			}
		});

		// Close search when clicking outside or on escape
		document.addEventListener("click", (e) => {
			if (this.#isSearchOpen && !e.target.closest(".Header-search")) {
				this.#closeSearch();
			}
		});

		searchInput.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && this.#isSearchOpen) {
				this.#closeSearch();
			}
		});
	}

	/**
	 * Hide any visible autocomplete dropdown and blur the search input
	 */
	#hideAutocomplete() {
		const dropdown = this.querySelector(".Header-autocomplete--visible");
		if (dropdown) {
			dropdown.classList.remove("Header-autocomplete--visible");
		}

		const searchInput = this.querySelector(".Header-searchInput");
		if (searchInput && searchInput === document.activeElement) {
			searchInput.blur();
		}
	}

	/**
	 * Close the search field in compact mode
	 */
	#closeSearch() {
		this.#isSearchOpen = false;
		this.#headerContainer.classList.remove("Header-searchOpen");
	}

	#updateHeaderState(currentScrollY, scrollDifference) {
		// If at the very top, return to normal (non-fixed) state
		if (currentScrollY <= this.#RESTORE_HEADER_THRESHOLD) {
			// Only update state if it actually changed to prevent unnecessary updates
			if (this.#headerContainer.classList.contains("Header-fixed")) {
				this.#isCompact = false;
				this.#closeSearch();
				this.#headerContainer.classList.remove(
					"Header-fixed",
					"Header-compact",
				);
				// Remove header height when header returns to normal flow
				document.documentElement.style.removeProperty("--header-height");

				// Update Swiper when header state changes
				if (this.#navSwiper) {
					this.#navSwiper.update();
					this.#updateNavGradients();
				}
			}
			return;
		}

		// Below the threshold, header is always fixed
		// Compact state depends on scroll direction
		if (currentScrollY > this.#SCROLL_THRESHOLD) {
			const isCurrentlyFixed =
				this.#headerContainer.classList.contains("Header-fixed");

			// Scrolling down - compact only if scroll delta exceeds minimum
			if (scrollDifference > this.#MIN_SCROLL_DELTA && !this.#isCompact) {
				this.#isCompact = true;
				this.#hideAutocomplete();
				this.#headerContainer.classList.add("Header-compact");

				if (!isCurrentlyFixed) {
					this.#headerContainer.classList.add("Header-fixed");
					// Set header height when header becomes fixed
					this.#updateHeaderHeight();
				}

				// Update Swiper when header state changes
				if (this.#navSwiper) {
					this.#navSwiper.update();
					this.#updateNavGradients();
				}
			}
			// Scrolling up - expand only if scroll delta exceeds minimum
			else if (scrollDifference < -this.#MIN_SCROLL_DELTA && this.#isCompact) {
				this.#isCompact = false;
				this.#closeSearch();
				this.#hideAutocomplete();
				this.#headerContainer.classList.remove("Header-compact");
				// Update header height when expanding from compact
				this.#updateHeaderHeight();

				// Update Swiper when header state changes
				if (this.#navSwiper) {
					this.#navSwiper.update();
					this.#updateNavGradients();
				}
			}
			// Just scrolled past threshold - make it fixed but not compact
			else if (!isCurrentlyFixed) {
				this.#headerContainer.classList.add("Header-fixed");
				this.#updateHeaderHeight();
			}
		}
	}

	#onScroll = () => {
		const currentScrollY = window.scrollY;
		const scrollDifference = currentScrollY - this.#lastScrollY;

		this.#updateHeaderState(currentScrollY, scrollDifference);
		this.#lastScrollY = currentScrollY;
	};
}

if (!customElements.get("beck-page-header")) {
	customElements.define("beck-page-header", BeckPageHeader);
}
